import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import styled from "styled-components";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from 'axios';
import constants from '../../const.js';
import dayjs from 'dayjs';

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 600px;
    width: 95vw;
  }

  .modal-content {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border: none;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px 20px;
  }

  .modal-header .modal-title {
    font-weight: 700;
    font-size: 18px;
    color: white;
  }

  .modal-header .btn-close {
    filter: brightness(0) invert(1);
  }

  .modal-body {
    background: #f8f9fb;
    padding: 20px;
    max-height: 85vh;
    overflow-y: auto;
  }

  .modal-footer {
    background: #f8f9fb;
    border-top: 1px solid #e0e0e0;
    padding: 12px 20px;
  }
`;

const ModernFormGroup = styled(Form.Group)`
  margin-bottom: 16px;

  .form-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-control {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    transition: all 0.3s ease;
    background-color: #ffffff;

    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 0.1rem rgba(102, 126, 234, 0.15);
      background-color: #ffffff;
    }
  }

  textarea.form-control {
    resize: vertical;
    min-height: 100px;
  }
`;

const ModernButton = styled(Button)`
  border-radius: 6px;
  font-weight: 600;
  padding: 8px 16px;
  transition: all 0.3s ease;
  border: none;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.btn-secondary {
    background: #95a5a6;
    color: white;

    &:hover:not(:disabled) {
      background: #7f8c8d;
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin: 12px 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #667eea;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

function CreateAlbumModal(props) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    folder_name: '',
    title: '',
    description: '',
    date: dayjs().format('YYYY-MM-DD'),
    rating: 0
  });

  const handleShow = () => {
    setShow(true);
    setError(null);
    setFormData({
      folder_name: '',
      title: '',
      description: '',
      date: dayjs().format('YYYY-MM-DD'),
      rating: 0
    });
  };

  const handleClose = () => {
    setShow(false);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) || 0 : value
    }));
  };

  const handleCreateAlbum = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate inputs
      if (!formData.folder_name.trim()) {
        setError('Album folder name is required');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Call API to create album with folder
      const response = await axios.post(
        `${constants.DATA1_API_URL}/gallery/${encodeURIComponent(props.galleryId)}/album/create-with-folder`,
        {
          folder_name: formData.folder_name,
          title: formData.title || formData.folder_name,
          description: formData.description,
          date: formData.date,
          rating: formData.rating
        },
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success) {
        console.log('✓ Album created successfully:', response.data);
        handleClose();
        
        // Call the onSave callback to refresh the gallery
        if (props.onSave) {
          props.onSave();
        }
      } else {
        setError('Failed to create album: ' + (response.data?.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error creating album:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create album');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModernButton 
        variant="primary" 
        onClick={handleShow}
        title="Create a new album"
      >
        <AddCircleOutlineIcon style={{ fontSize: '18px' }} />
        New Album
      </ModernButton>

      <StyledModal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Album</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <SectionTitle>Album Details</SectionTitle>
          
          <ModernFormGroup>
            <Form.Label>Album Folder Name *</Form.Label>
            <Form.Control
              type="text"
              name="folder_name"
              value={formData.folder_name}
              onChange={handleInputChange}
              placeholder="e.g., my-album-2024"
              disabled={loading}
            />
            <small className="text-muted">
              This will be the folder name on the server (no spaces, use hyphens)
            </small>
          </ModernFormGroup>

          <ModernFormGroup>
            <Form.Label>Album Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Album title (optional)"
              disabled={loading}
            />
            <small className="text-muted">
              If not provided, folder name will be used
            </small>
          </ModernFormGroup>

          <ModernFormGroup>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Album description (optional)"
              disabled={loading}
              rows={3}
            />
          </ModernFormGroup>

          <Row>
            <Col md={6}>
              <ModernFormGroup>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </ModernFormGroup>
            </Col>
            <Col md={6}>
              <ModernFormGroup>
                <Form.Label>Rating</Form.Label>
                <Form.Control
                  type="number"
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                  min="0"
                  max="5"
                  disabled={loading}
                />
              </ModernFormGroup>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <ModernButton 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </ModernButton>
          <ModernButton 
            variant="primary" 
            onClick={handleCreateAlbum}
            disabled={loading || !formData.folder_name.trim()}
          >
            {loading ? (
              <>
                <Spinner 
                  as="span" 
                  animation="border" 
                  size="sm" 
                  role="status" 
                  aria-hidden="true"
                  style={{ marginRight: '8px' }}
                />
                Creating...
              </>
            ) : (
              <>
                <AddCircleOutlineIcon style={{ fontSize: '18px' }} />
                Create Album
              </>
            )}
          </ModernButton>
        </Modal.Footer>
      </StyledModal>
    </>
  );
}

export default CreateAlbumModal;
